// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The player's own hands, drawn (14 Sep 2026).
//
// Nothing drew them. In VR that meant a player had no hands at all; in mixed reality it meant
// something worse, found on the Quest 3 the day the start button moved to the front door: "in MR,
// user's hands disappear (hidden) when over left-palm and machine". The passthrough camera shows
// the real hand, the renderer draws the panel and the cabinet on top of the passthrough, and the
// browser has no depth of the real hand to say which is in front — so a hand reaching for the
// start button vanished behind the door it was reaching for, and a hand over the wrist panel
// vanished behind the panel it was holding up. A person cannot press what they cannot see their
// hand on.
//
// **Ghost hands, not occlusion.** The proper answer in mixed reality is depth — the headset knows
// where the real hand is and the renderer leaves a hole for it — and the native client gets that
// from Meta's Depth API for nothing. WebXR has a `depth-sensing` module that could do the same,
// unverified on this browser and paid for in frame time; this is the design bench, and the bench
// needs a hand it can see today in both modes. So the tracked joints are drawn as a translucent
// hand in the same reference space the machine stands in, which is exactly where the passthrough
// hand is. Two passes: one that respects depth, so a hand in front of the cabinet reads as in
// front; and a fainter one that ignores it, so a hand behind the door or under the panel still
// shows through as a ghost, where it is.
//
// **A hand, not a skeleton** (the 17:09 session: "hands show as skeletal, is it possible to show
// more like a hand?"). The first cut drew sticks thinner than the joints. Now each finger is as
// wide as its joints — the capsule between two joints takes their mean radius, and the sphere at
// each joint rounds the knuckle — and the palm is a plate: the eight joints around its rim (the
// wrist, the thumb and index metacarpals, the four proximal knuckles, the pinky metacarpal) as a
// fan on two faces a palm's thickness apart, walled at the rim. The depth pass writes depth, so
// where a sphere, a capsule and the plate overlap only the nearest surface is seen and the hand
// reads as one translucent solid rather than a pile of glass parts. All of it from the twenty-five
// joints the headset already reports: no model to download, nothing that can be a centimetre off
// from where the hand actually is.
//
// **Instanced, no per-frame allocation.** Fifty spheres and fifty-four capsules are a hundred and
// four matrices a frame, written into four `InstancedMesh`es; the two palms are thirty-six
// vertices rewritten in place. The `xr` tier's frame budget does not notice. Unlit, so it reads
// the same over passthrough and inside the venue, and slightly blue-white so it is a hologram and
// not a glove.

import {
  BufferAttribute,
  BufferGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  InstancedMesh,
  Material,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Quaternion,
  SphereGeometry,
  Vector3,
} from 'three'
import { HAND_BONES, HAND_JOINT_COUNT, type HandJoints } from '../xr/handJoints'

/** The hand's colour: blue-white, a hologram rather than a glove. */
export const GHOST_HAND_COLOUR = 0xbfe3ff
/** The pass that respects depth. */
export const GHOST_SOLID_OPACITY = 0.6
/** The pass that shows through whatever is in front. */
export const GHOST_XRAY_OPACITY = 0.18
/** A capsule is as wide as the mean of the two joints it joins, times this. */
export const BONE_RADIUS_SCALE = 1
/** Half a palm's thickness, metres: the plate's two faces sit this far either side of the joints. */
export const PALM_HALF_THICKNESS = 0.011

/**
 * The joints around the palm, in order round its rim: wrist, thumb metacarpal, index metacarpal,
 * then the four proximal knuckles index to pinky, then the pinky metacarpal back to the wrist.
 */
export const PALM_RIM: readonly number[] = [0, 1, 5, 6, 11, 16, 21, 20]

const HANDS = 2
const SPHERES = HANDS * HAND_JOINT_COUNT
const BONES = HANDS * HAND_BONES.length
const RIM = PALM_RIM.length
/** Two faces of rim + centre each. */
const PALM_VERTICES = 2 * (RIM + 1)

export interface GhostHandsBuild {
  readonly group: Group
  readonly geometries: readonly BufferGeometry[]
  readonly materials: readonly Material[]
  /** The solid pass's instanced meshes, for a test to read a matrix off. Index 0 joints, 1 bones. */
  readonly solid: readonly [InstancedMesh, InstancedMesh]
  /** The see-through pass's, likewise. */
  readonly xray: readonly [InstancedMesh, InstancedMesh]
  /** The palm plates of the solid pass, left then right; the see-through pass shares their geometry. */
  readonly palms: readonly [Mesh, Mesh]
  /** Put every joint, bone and palm where the frame says; a hand not present is hidden. */
  update: (left: HandJoints, right: HandJoints) => void
  /** Hide both hands — the session ended, and the flat screen must not keep the last pose. */
  hide: () => void
  dispose: () => void
}

const HIDDEN = new Matrix4().makeScale(0, 0, 0)
const UP = new Vector3(0, 1, 0)

/** The palm plate's triangles, fixed: a fan on each face and a quad strip round the rim. */
function palmIndex(): number[] {
  const top = 0
  const bottom = RIM + 1
  const out: number[] = []
  for (let i = 0; i < RIM; i += 1) {
    const a = top + 1 + i
    const b = top + 1 + ((i + 1) % RIM)
    out.push(top, a, b)
    const c = bottom + 1 + i
    const d = bottom + 1 + ((i + 1) % RIM)
    out.push(bottom, d, c)
    // The wall between rim edge (a, b) on top and (c, d) below.
    out.push(a, c, d, a, d, b)
  }
  return out
}

export function buildGhostHands(): GhostHandsBuild {
  const group = new Group()
  group.name = 'ghost-hands'
  // Unit sphere and unit cylinder along Y; every instance is a scale and a placement of one.
  const sphere = new SphereGeometry(1, 10, 8)
  const bone = new CylinderGeometry(1, 1, 1, 8, 1, false)
  const solidMaterial = new MeshBasicMaterial({
    color: GHOST_HAND_COLOUR,
    transparent: true,
    opacity: GHOST_SOLID_OPACITY,
    depthTest: true,
    // Written, so the parts of one hand resolve to a single surface instead of stacking alpha
    // where a knuckle sits in a capsule sits in the palm.
    depthWrite: true,
    // The palm plate's winding runs the other way round on the other hand.
    side: DoubleSide,
  })
  const xrayMaterial = new MeshBasicMaterial({
    color: GHOST_HAND_COLOUR,
    transparent: true,
    opacity: GHOST_XRAY_OPACITY,
    depthTest: false,
    depthWrite: false,
    side: DoubleSide,
  })
  const instanced = (geometry: BufferGeometry, material: Material, count: number, order: number, name: string): InstancedMesh => {
    const m = new InstancedMesh(geometry, material, count)
    // The instances move every frame and the bounding sphere is never recomputed; a hand that
    // is culled because its geometry's own bounds are a unit sphere at the origin is no hand.
    m.frustumCulled = false
    m.renderOrder = order
    m.name = name
    for (let i = 0; i < count; i += 1) m.setMatrixAt(i, HIDDEN)
    m.instanceMatrix.needsUpdate = true
    group.add(m)
    return m
  }
  const solid: [InstancedMesh, InstancedMesh] = [
    instanced(sphere, solidMaterial, SPHERES, 999, 'ghost-joints'),
    instanced(bone, solidMaterial, BONES, 999, 'ghost-bones'),
  ]
  const xray: [InstancedMesh, InstancedMesh] = [
    instanced(sphere, xrayMaterial, SPHERES, 1000, 'ghost-joints-xray'),
    instanced(bone, xrayMaterial, BONES, 1000, 'ghost-bones-xray'),
  ]

  // One palm geometry per hand, drawn by both passes.
  const palmGeometries: BufferGeometry[] = []
  const palms: Mesh[] = []
  const palmsXray: Mesh[] = []
  for (const side of ['left', 'right'] as const) {
    const g = new BufferGeometry()
    g.setAttribute('position', new BufferAttribute(new Float32Array(PALM_VERTICES * 3), 3))
    g.setIndex(palmIndex())
    palmGeometries.push(g)
    const plate = new Mesh(g, solidMaterial)
    plate.frustumCulled = false
    plate.renderOrder = 999
    plate.name = `ghost-palm-${side}`
    plate.visible = false
    group.add(plate)
    palms.push(plate)
    const shade = new Mesh(g, xrayMaterial)
    shade.frustumCulled = false
    shade.renderOrder = 1000
    shade.name = `ghost-palm-${side}-xray`
    shade.visible = false
    group.add(shade)
    palmsXray.push(shade)
  }

  const matrix = new Matrix4()
  const position = new Vector3()
  const scale = new Vector3()
  const direction = new Vector3()
  const rotation = new Quaternion()
  const centre = new Vector3()
  const edge1 = new Vector3()
  const edge2 = new Vector3()
  const normal = new Vector3()

  const setJoint = (index: number, m: Matrix4): void => {
    solid[0].setMatrixAt(index, m)
    xray[0].setMatrixAt(index, m)
  }
  const setBone = (index: number, m: Matrix4): void => {
    solid[1].setMatrixAt(index, m)
    xray[1].setMatrixAt(index, m)
  }

  const placePalm = (hand: HandJoints, slot: number): void => {
    const plate = palms[slot]!
    const shade = palmsXray[slot]!
    if (!hand.present || PALM_RIM.some((j) => hand.seen[j] !== 1)) {
      plate.visible = false
      shade.visible = false
      return
    }
    const attribute = palmGeometries[slot]!.getAttribute('position') as BufferAttribute
    const out = attribute.array as Float32Array
    centre.set(0, 0, 0)
    for (const j of PALM_RIM) centre.add(position.set(hand.at[j * 3]!, hand.at[j * 3 + 1]!, hand.at[j * 3 + 2]!))
    centre.divideScalar(RIM)
    // The palm's normal: across the hand from the wrist, index knuckle × pinky knuckle.
    const w = PALM_RIM[0]!
    const index = PALM_RIM[3]!
    const pinky = PALM_RIM[6]!
    edge1.set(hand.at[index * 3]! - hand.at[w * 3]!, hand.at[index * 3 + 1]! - hand.at[w * 3 + 1]!, hand.at[index * 3 + 2]! - hand.at[w * 3 + 2]!)
    edge2.set(hand.at[pinky * 3]! - hand.at[w * 3]!, hand.at[pinky * 3 + 1]! - hand.at[w * 3 + 1]!, hand.at[pinky * 3 + 2]! - hand.at[w * 3 + 2]!)
    normal.crossVectors(edge1, edge2)
    const n = normal.length()
    if (n < 1e-9) {
      plate.visible = false
      shade.visible = false
      return
    }
    normal.divideScalar(n).multiplyScalar(PALM_HALF_THICKNESS)
    const write = (vertex: number, x: number, y: number, z: number, sign: number): void => {
      out[vertex * 3] = x + normal.x * sign
      out[vertex * 3 + 1] = y + normal.y * sign
      out[vertex * 3 + 2] = z + normal.z * sign
    }
    write(0, centre.x, centre.y, centre.z, 1)
    write(RIM + 1, centre.x, centre.y, centre.z, -1)
    for (let i = 0; i < RIM; i += 1) {
      const j = PALM_RIM[i]!
      const x = hand.at[j * 3]!
      const y = hand.at[j * 3 + 1]!
      const z = hand.at[j * 3 + 2]!
      write(1 + i, x, y, z, 1)
      write(RIM + 2 + i, x, y, z, -1)
    }
    attribute.needsUpdate = true
    plate.visible = true
    shade.visible = true
  }

  const place = (hand: HandJoints, slot: number): void => {
    const jointBase = slot * HAND_JOINT_COUNT
    const boneBase = slot * HAND_BONES.length
    placePalm(hand, slot)
    if (!hand.present) {
      for (let i = 0; i < HAND_JOINT_COUNT; i += 1) setJoint(jointBase + i, HIDDEN)
      for (let b = 0; b < HAND_BONES.length; b += 1) setBone(boneBase + b, HIDDEN)
      return
    }
    for (let i = 0; i < HAND_JOINT_COUNT; i += 1) {
      if (hand.seen[i] !== 1) {
        setJoint(jointBase + i, HIDDEN)
        continue
      }
      const r = hand.radius[i]!
      position.set(hand.at[i * 3]!, hand.at[i * 3 + 1]!, hand.at[i * 3 + 2]!)
      scale.set(r, r, r)
      matrix.compose(position, rotation.identity(), scale)
      setJoint(jointBase + i, matrix)
    }
    for (let b = 0; b < HAND_BONES.length; b += 1) {
      const [a, c] = HAND_BONES[b]!
      if (hand.seen[a] !== 1 || hand.seen[c] !== 1) {
        setBone(boneBase + b, HIDDEN)
        continue
      }
      const ax = hand.at[a * 3]!
      const ay = hand.at[a * 3 + 1]!
      const az = hand.at[a * 3 + 2]!
      direction.set(hand.at[c * 3]! - ax, hand.at[c * 3 + 1]! - ay, hand.at[c * 3 + 2]! - az)
      const length = direction.length()
      if (length < 1e-6) {
        setBone(boneBase + b, HIDDEN)
        continue
      }
      direction.divideScalar(length)
      position.set(ax + (direction.x * length) / 2, ay + (direction.y * length) / 2, az + (direction.z * length) / 2)
      const r = ((hand.radius[a]! + hand.radius[c]!) / 2) * BONE_RADIUS_SCALE
      scale.set(r, length, r)
      rotation.setFromUnitVectors(UP, direction)
      matrix.compose(position, rotation, scale)
      setBone(boneBase + b, matrix)
    }
  }

  const flag = (): void => {
    for (const m of solid) m.instanceMatrix.needsUpdate = true
    for (const m of xray) m.instanceMatrix.needsUpdate = true
  }

  const update = (left: HandJoints, right: HandJoints): void => {
    place(left, 0)
    place(right, 1)
    flag()
  }

  const hide = (): void => {
    for (let i = 0; i < SPHERES; i += 1) setJoint(i, HIDDEN)
    for (let b = 0; b < BONES; b += 1) setBone(b, HIDDEN)
    for (const plate of palms) plate.visible = false
    for (const shade of palmsXray) shade.visible = false
    flag()
  }

  const geometries = [sphere, bone, ...palmGeometries]
  const materials = [solidMaterial, xrayMaterial]
  const dispose = (): void => {
    for (const m of [...solid, ...xray]) m.dispose()
    for (const g of geometries) g.dispose()
    for (const m of materials) m.dispose()
  }

  return { group, geometries, materials, solid, xray, palms: [palms[0]!, palms[1]!], update, hide, dispose }
}
